import React, { useState, useEffect, useMemo } from 'react';
import theme from "../../theme";
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import ChartComponent from './Chart';
import FilterForm, { bandsToParams } from './FilterForm';
import TableComponent from './TableComponent';
import { analysisConfig } from './AnalysisConfig';
import {
    getClientAnalysisAction,
    getRevenueAnalysisAction,
    getTransactionAnalysisAction,
    getUserStatsAction,
    getSalaryRevenueAnalysisAction,
    getCompanyAnalysisAction,
    resetAnalysisErrorAction,
} from '../../containers/reports/action';
import { selectAnalysisForEndpoint, selectCompanyAnalysis } from '../../containers/reports/selectors';
import { getStartEndDate } from '../../utils';

// One place to look up "what action fetches this endpoint" — keeps the
// switch out of the useEffect and makes it obvious which report each dispatch
// belongs to.
const ANALYSIS_DISPATCH = {
    clientanalysis: getClientAnalysisAction,
    revenueanalysis: getRevenueAnalysisAction,
    transactionanalysis: getTransactionAnalysisAction,
    userstats: getUserStatsAction,
    salaryrevenueanalysis: getSalaryRevenueAnalysisAction,
    companyanalysis: getCompanyAnalysisAction,
};

// endpoint segment → analysis key used by resetAnalysisErrorAction. Kept
// separate from ANALYSIS_DISPATCH so a rename of one doesn't quietly
// misalign the other.
const ANALYSIS_KEY = {
    clientanalysis: "client",
    revenueanalysis: "revenue",
    transactionanalysis: "transaction",
    userstats: "user_stats",
    salaryrevenueanalysis: "salary_revenue",
    companyanalysis: "company",
};

const useStyles = makeStyles(() => ({
    root: {
        backgroundColor: theme.colors.White,
        width: "100%",
        minHeight: "calc(100vh - 185px)",
        padding: "45px 90px",
        marginLeft: "220px",
    },
    select: {
        padding: "8px 12px",
        borderRadius: "8px",
        border: "1px solid #D1D5DB",
        fontSize: "14px",
        fontWeight: "500",
        width: "200px",
        marginBottom: "16px",
        outline: "none",
        '&:hover': {
            borderColor: "#10B981",
        },
        '&:focus': {
            borderColor: "#10B981",
        },
    },
    label: {
        fontSize: "16px",
        fontWeight: "600",
        marginBottom: "8px",
        marginRight: '3px',
        color: "#333",
    },
    backdrop: {
        zIndex: 999999,
        color: "#fff",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    circularProgress: {
        display: "flex",
        justifyContent: "center",
        alignContent: "center",
        color: theme.colors.DodgerBlue,
    },
    h2: {
        fontSize: "1.125rem",
        fontWeight: 600,
        marginTop: "1.5rem",
    },
    noGraph: {
        fontSize: "1rem",
        fontWeight: 500,
        color: "#888",
        textAlign: "center",
        marginTop: "20px",
    }
}));


const AdminReports = () => {
    const [start_date, end_date] = getStartEndDate();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [endpoint, setEndpoint] = useState('revenueanalysis');
    const [filterStates, setFilterStates] = useState({
        revenueanalysis: {},
        companyanalysis: {},
        clientanalysis: {},
        transactionanalysis: {},
        userstats: {},
        salaryrevenueanalysis: {}
    });
    const [validationError, setValidationError] = useState('');

    // Company list feeds the company/client filter dropdowns. Sourced from
    // the companyanalysis endpoint just like before, only now via the saga
    // pipeline (no direct axios). We dispatch on mount and read the same
    // reducer slot the companyanalysis report uses — so both the filter
    // dropdown and the table share one cached payload.
    const companyAnalysis = useSelector(selectCompanyAnalysis);
    const analysisSlice = useSelector(selectAnalysisForEndpoint(endpoint));

    const companies = useMemo(() => {
        const rows = Array.isArray(companyAnalysis?.data) ? companyAnalysis.data : [];
        return rows.map((c) => ({ value: c.company, label: c.company }));
    }, [companyAnalysis?.data]);

    // Merge the freshly loaded company options into the per-endpoint filter
    // config. `analysisConfig` stays the source of truth for column/xKey/etc;
    // we only inject options for company-scoped filters.
    const config = useMemo(() => {
        const next = {};
        Object.keys(analysisConfig).forEach((key) => {
            const entry = analysisConfig[key];
            const filters = (entry.filters || []).map((filter) => {
                const isCompanyFilter =
                    (filter.name === "company_id" || filter.name === "company") &&
                    (filter.type === "select" || filter.type === "dropdown");
                if (isCompanyFilter) {
                    return { ...filter, type: "select", options: companies };
                }
                return filter;
            });
            next[key] = { ...entry, filters };
        });
        return next;
    }, [companies]);

    const { columns, xKey, barKey, chartType, filters: filterConfig, pathName } = config[endpoint];

    // Prime the company list once. The reducer keeps it around so switching
    // endpoints doesn't re-fetch.
    useEffect(() => {
        if (!companyAnalysis?.data && !companyAnalysis?.loading) {
            dispatch(getCompanyAnalysisAction({}));
        }
    }, [dispatch, companyAnalysis?.data, companyAnalysis?.loading]);

    // Any pending 400/404 for the current endpoint is stale as soon as the
    // operator opens a different report — clear it so we don't paint a
    // previous report's errors on the new one.
    useEffect(() => {
        if (analysisSlice?.error || analysisSlice?.fieldErrors) {
            dispatch(resetAnalysisErrorAction(ANALYSIS_KEY[endpoint]));
        }
        setValidationError("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint]);

    // Fetch whenever endpoint/filters change. Date defaults are computed
    // here rather than in the saga so all reports get the same first-open
    // window without the saga having to know per-endpoint semantics.
    useEffect(() => {
        const stateFilter = filterStates[endpoint] || {};
        const needsDateDefault =
            (filterConfig || []).some((f) => f.type === "date");
        // Flatten the internal `bands` object (mode + ranges/buckets) into
        // the query-param shape the API expects (?ranges=... or ?buckets=N).
        // The bands blob itself never leaves the component.
        const { bands, ...restFilter } = stateFilter;
        const bandsParams = bands ? bandsToParams(bands) : {};
        const payload = needsDateDefault
            ? {
                ...restFilter,
                ...bandsParams,
                start_date: restFilter.start_date || start_date.toISOString().split("T")[0],
                end_date: restFilter.end_date || end_date.toISOString().split("T")[0],
            }
            : { ...restFilter, ...bandsParams };

        const dispatcher = ANALYSIS_DISPATCH[endpoint];
        if (dispatcher) dispatch(dispatcher(payload));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, filterStates, filterConfig]);

    const handleFilterChange = (newFilters) => {
        // Filter edits invalidate any lingering server-side error for the
        // active report — otherwise the field-error would keep showing next
        // to an input the operator just changed.
        if (analysisSlice?.error || analysisSlice?.fieldErrors) {
            dispatch(resetAnalysisErrorAction(ANALYSIS_KEY[endpoint]));
        }
        setFilterStates((prev) => ({ ...prev, [endpoint]: newFilters }));
    };

    const rows = Array.isArray(analysisSlice?.data) ? analysisSlice.data : [];
    const isLoading = !!analysisSlice?.loading;
    const topLevelError = analysisSlice?.error || null;
    const fieldErrors = analysisSlice?.fieldErrors || null;

    return (
        <div className={classes.root}>
            {topLevelError ? (
                <div className={"error-container"} style={{ marginBottom: 12 }}>
                    <div>{topLevelError}</div>
                </div>
            ) : null}
            <h1 className="text-2xl font-bold mb-4 capitalize">{pathName}</h1>

            <label htmlFor="endpoint" className={classes.label}>
                Select Analysis Type
            </label>
            <select
                className={classes.select}
                id="endpoint"
                value={endpoint}
                onChange={(e) => {
                    setEndpoint(e.target.value);
                    setValidationError("");
                }}
            >
                {Object.keys(config).map((key) => (
                    <option value={key} key={key}>
                        {key === "salaryrevenueanalysis"
                            ? "Salary Revenue"
                            : key === "userstats"
                                ? "User Statistics"
                                : key.includes("analysis")
                                    ? key.replace("analysis", "")[0].toUpperCase() +
                                    key.slice(1, -8)
                                    : key[0].toUpperCase() + key.slice(1)}
                    </option>
                ))}
            </select>

            <FilterForm
                filters={filterStates[endpoint]}
                setFilters={handleFilterChange}
                filterConfig={filterConfig}
                validationError={validationError}
                fieldErrors={fieldErrors}
            />

            {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                    <CircularProgress size={28} style={{ color: theme.colors.DodgerBlue }} />
                </div>
            ) : null}

            {!isLoading && chartType && rows.length > 0 && (
                <>
                    <h2 className={classes.h2}>Data Graph</h2>
                    <ChartComponent
                        data={rows}
                        chartType={chartType}
                        xKey={xKey}
                        dataKey={barKey}
                    />
                </>
            )}

            <h2 className={classes.h2}>Data Table</h2>
            {!isLoading && rows.length > 0 ? (
                <TableComponent data={rows} columns={columns} endpoint={endpoint} />
            ) : !isLoading ? (
                <div className={classes.noGraph}>No data for the current filters.</div>
            ) : null}
        </div>

    );
};
export default AdminReports;